import { PERMANENT_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { HTTPStatuses } from '@packages/domain/constants/http';
import { url } from '@packages/domain/utils/url/url';
import { taxErrorNotification } from 'actions/errors';
import { enableFeature } from 'actions/features';
import {
  ADD_ORDER_TO_USER_FAILURE,
  ADD_ORDER_TO_USER_REQUEST,
  ADD_ORDER_TO_USER_SUCCESS,
  CREATE_ORDER_FAILURE,
  MAINTENANCE_MODE,
  UPDATE_ORDER_FAILURE,
  UPDATE_ORDER_REQUEST,
  UPDATE_ORDER_SUCCESS,
} from 'constants/index';
import { schemas } from 'constants/schemas';
import isEmpty from 'lodash/isEmpty';
import type { Field, FieldEntityType, Item } from 'models';
import { normalize } from 'normalizr';
import type { NormalizedEntity, RootState } from 'reducers/types';
import type { Dispatch } from 'redux';
import {
  getCurrentLocale,
  getFieldByEntity,
  getFieldById,
  getFieldValue,
  getItems,
  getOrder,
  getOrderSectionFieldValue,
  isEmbedded,
  isFetching,
  isReadOnlyOrderSectionField,
  isThereAnErrorRetrievingTaxes,
  shouldHideUserManagement,
} from 'selectors';
import type {
  CreatePayload,
  OfferEntityFields,
  OrderServiceError,
  RecipientEntityFields,
  UpdatePayload,
} from 'services/apiV3/orders/orders';
import type { StoreServices } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

type APIError = {
  parsedBody?: { errors?: OrderServiceError[] };
  status: number;
};

const alreadyFetchingOrder = () =>
  Promise.reject(new Error('Already fetching order...'));

const hasOrderValueChanged = (field: Field, state: RootState) => {
  const orderFieldValue = getOrderSectionFieldValue(field.id, state);

  if (!orderFieldValue) {
    return !!field.defaultValue;
  }

  return field.defaultValue !== orderFieldValue.value;
};

const editableFields = (field: Field) => !field.apiReadOnly;

const normalizeEntity = (fields: NormalizedEntity<Field>, state: RootState) => {
  const getFieldsById = (fieldId: string) => getFieldById(state, fieldId);

  const filledFields = (field: Field) =>
    hasOrderValueChanged(field, state) || !!field.defaultValue;

  const normalizeFields = (field: Field) => ({
    id: field.name,
    value: field.defaultValue,
  });

  const normalizedFields = fields.ids
    .map(getFieldsById)
    .filter(filledFields)
    .filter(editableFields)
    .map(normalizeFields);

  return normalizedFields.length ? { fields: normalizedFields } : {};
};

export const entitiesPayload = (
  entityOrEntities: FieldEntityType | FieldEntityType[],
  state: RootState,
  isUpdate = false,
) => {
  const fingerprint = cookies.read(PERMANENT_COOKIE_NAME);
  const entities = Array.isArray(entityOrEntities)
    ? entityOrEntities
    : new Array(entityOrEntities);

  const payload = entities.reduce((acc, entity) => {
    const fields = getFieldByEntity(state, entity);
    const normalizedPayload = normalizeEntity(fields, state);

    if (entity === 'sender') {
      if (!isUpdate || !isReadOnlyOrderSectionField(state, 'sender_country')) {
        const selectedCountry = getFieldValue(state, 'sender_country');
        const countryField = { id: 'country', value: selectedCountry };
        const { fields = [] } = normalizedPayload;
        normalizedPayload.fields = [...fields, countryField];
      }

      const areTermsAccepted = !!getFieldValue(state, 'are_terms_accepted');
      if (areTermsAccepted) {
        const termsField = {
          id: 'are_terms_accepted',
          value: areTermsAccepted,
        };
        normalizedPayload.fields = normalizedPayload.fields || [];
        normalizedPayload.fields.push(termsField);
      }
    }

    if (entity === 'offers') {
      entity = 'offer';
      const offerValue = getFieldValue(state, entity as string);
      (normalizedPayload as OfferEntityFields).id = offerValue as string;
    }

    if (entity === 'recipient') {
      const items = getItems(state);

      const itemsPayload = items.filter(editableFields).map(item => {
        return { id: item.id, amount: Number(getFieldValue(state, item.id)) };
      });

      if (itemsPayload.length) {
        (normalizedPayload as RecipientEntityFields).items = itemsPayload;
      }
    }

    return { ...acc, ...{ [entity]: normalizedPayload }, fingerprint };
  }, {} as CreatePayload);

  const locale = getCurrentLocale(state);
  const provider = getFieldValue(state, 'provider');
  if (provider) {
    payload.provider = provider as string;
  }

  if (isEmbedded(state)) {
    const callbackUrl = getFieldValue(state, 'callback_url');
    const callbackVersion = getFieldValue(state, 'callback_version');

    const hasCallbackUrl = callbackUrl && (callbackUrl as string).length > 0;
    const isCallbackV2 = callbackVersion === '2';

    const statusChangeCallback = {
      id: getFieldValue(state, 'callback_id') as string,
      ...(hasCallbackUrl ? { url: callbackUrl as string } : {}),
      ...(isCallbackV2 ? { version: '2' } : {}),
    };

    payload.callback = statusChangeCallback;
  }

  return {
    ...payload,
    locale,
  };
};

const _unexpectedError = (error: APIError, dispatch: Dispatch) => {
  dispatch({ type: CREATE_ORDER_FAILURE, payload: {} });
  throw error;
};

const _paymentInformationError = (
  error: { parsedBody?: { errors?: OrderServiceError[] } },
  dispatch: Dispatch,
) => {
  const { parsedBody: { errors } = {} } = error;
  const payload = normalize(errors, schemas.arrayOfErrors);

  dispatch({ type: CREATE_ORDER_FAILURE, payload });
  throw error;
};

const _removeInvalidFieldsFromPayload = (
  errors: OrderServiceError[] = [],
  payload: CreatePayload,
) => {
  const newPayload = { ...payload };
  const COUNTRY_SOURCE = '/sender/fields/country';
  const { sender, recipient, offer } = newPayload;

  const omitCountry = (error: OrderServiceError) =>
    error.source !== COUNTRY_SOURCE;
  const fieldsToRemove = errors.filter(omitCountry).map(error => error.param);

  const isValid = (field: Field) => !fieldsToRemove.includes(field.id);
  const newSenderFields = sender.fields.filter(isValid);
  const newRecipientFields = recipient.fields
    ? recipient.fields.filter(isValid)
    : [];
  const newOfferFields = offer?.fields ? offer.fields.filter(isValid) : [];

  newPayload.sender.fields = newSenderFields;
  newPayload.recipient.fields = newRecipientFields;
  if (!isEmpty(offer)) {
    newPayload.offer.fields = newOfferFields;
  }

  return newPayload;
};

const returnUrl = (id: string, token: string, hideUserManagement: boolean) => {
  const domain = url.getCurrentUrl();
  let trackingUrl = `${domain}/tracking/${id}?token=${token}`;

  if (hideUserManagement === true) {
    trackingUrl = `${trackingUrl}&hideUserManagement=true`;
  }

  return trackingUrl;
};

const updateOrder =
  (payload: Omit<UpdatePayload, 'orderId' | 'token'>) =>
  (dispatch: Dispatch, getState: () => RootState, { apiV3 }: StoreServices) => {
    const state = getState();
    const isFetchingOrder = isFetching(state, 'order');

    if (isFetchingOrder) {
      return alreadyFetchingOrder();
    }

    dispatch({ type: UPDATE_ORDER_REQUEST });

    const { id, token } = getOrder(state);
    const hideUserManagement = shouldHideUserManagement(state);
    payload.return_url = returnUrl(id, token, hideUserManagement);
    payload.metadata = {
      tracking_url: returnUrl(id, token, hideUserManagement),
    };

    return apiV3.orders
      .update({ orderId: id, token, ...payload })
      .then(response =>
        dispatch({ type: UPDATE_ORDER_SUCCESS, payload: response }),
      )
      .catch(error => {
        if (error.status === 503) {
          return dispatch(enableFeature(MAINTENANCE_MODE));
        }

        const { parsedBody: { errors: validationErrors = [] } = {} } = error;

        error.message = `${UPDATE_ORDER_FAILURE} ${error.message}`;
        if (error.status >= HTTPStatuses.INTERNAL_SERVER_ERROR) {
          errorNotifier.notifyWithFingerprint(error, [
            UPDATE_ORDER_FAILURE,
            error.status,
          ]);
        }

        dispatch({
          type: UPDATE_ORDER_FAILURE,
          payload: normalize(validationErrors, schemas.arrayOfErrors),
        });

        if (isThereAnErrorRetrievingTaxes(getState())) {
          dispatch(taxErrorNotification());
        }

        throw error;
      });
  };

export const updateOrderWithEntities =
  (entities: string | string[]) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const payload = entitiesPayload(entities, state, true);

    return dispatch(updateOrder(payload));
  };

const paymentInformationPayload = (state: RootState) => {
  const locale = getCurrentLocale(state);

  const fingerprint = cookies.read(PERMANENT_COOKIE_NAME);

  const sender: { fields: { id: string; value: string }[] } = {
    fields: [],
  };
  if (!isReadOnlyOrderSectionField(state, 'sender_country')) {
    const selectedCountry = getFieldValue(state, 'sender_country') as string;
    sender.fields.push({ id: 'country', value: selectedCountry });
  }

  const formatItem = (item: Item) => {
    const { id } = item;
    const amount = Number(getFieldValue(state, item.id));

    return { id, amount };
  };
  const items = getItems(state);
  const itemsPayload = items.filter(editableFields).map(formatItem);
  const recipient = { items: itemsPayload };

  return { sender, recipient, locale, fingerprint };
};

export const updateOrderWithPaymentInformation =
  () => (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const payload = paymentInformationPayload(state);

    return dispatch(updateOrder(payload));
  };

export const addOrderToUser =
  () =>
  (
    dispatch: Dispatch,
    getState: () => RootState,
    { orders }: StoreServices,
  ) => {
    const state = getState();
    const order = getOrder(state);
    const { id: orderId, token } = order;

    dispatch({ type: ADD_ORDER_TO_USER_REQUEST });

    return orders
      .addToUser({ orderId, token })
      .then(payload => dispatch({ type: ADD_ORDER_TO_USER_SUCCESS, payload }))
      .then(response => response.payload)
      .catch(error => {
        error.message = `${ADD_ORDER_TO_USER_FAILURE} ${error.message}`;
        if (error.status !== HTTPStatuses.UNPROCESSABLE_ENTITY) {
          errorNotifier.notifyWithFingerprint(error, [
            ADD_ORDER_TO_USER_FAILURE,
            error.status,
          ]);
        }
      });
  };
